/**
 * Batch 18-I8 — Provider Evidence Display Helper.
 *
 * Pure mapping from I5 / I7 provider evidence summary rows
 * (`TrainingProviderEvidenceSummaryRow`) → provider evidence display
 * state (badges + state booleans). Surface-aware via the
 * `ProviderEvidenceSurface` discriminator.
 *
 * Trust contract (locked from I1 / I2 / I5 / I6 / I7):
 *   - Provider evidence is a PARALLEL badge family — never merged
 *     into the manager/manual union driven by G6 `completionSummary`
 *     and the H2 / G14 / G16 helpers.
 *   - This helper does NOT import / reference / overload the
 *     existing ticket / requirement / drawer-row / technician
 *     evidence-summary helpers (kept deliberately separate so
 *     provider state never flows into the manager/manual union).
 *   - This helper does NOT emit manager labels (`Verified by manager`,
 *     `Verification pending`, manager `Verification revoked`,
 *     `Manual completion submitted`), the content-quality chip
 *     (`OptiBay reviewed content`), or the certification/credential
 *     vocabulary (`Certified`, `Confidence`, `Previously completed`,
 *     standalone bare `Verified`).
 *   - This helper is pure: no React import, no Supabase import, no
 *     hook composition, no I/O, no Date.now() — surface-deterministic
 *     output for a given input.
 *   - The helper's input shape is the I7 hook's row type. Importing
 *     it from `@/hooks/useTrainingProviderEvidenceSummary` keeps a
 *     single source of truth for the closed-set
 *     `ProviderEvidenceState` union and the row column list.
 */

import type {
  TrainingProviderEvidenceSummaryRow,
  ProviderEvidenceState,
} from '@/hooks/useTrainingProviderEvidenceSummary';

// ─── Public types ───────────────────────────────────────────────────

export type ProviderEvidenceSurface =
  | 'ticket'
  | 'requirement'
  | 'detail'
  | 'admin';

export type ProviderEvidenceBadgeVariant =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'destructive';

export interface ProviderEvidenceDisplayBadge {
  label: string;
  testId: string;
  variant: ProviderEvidenceBadgeVariant;
  providerId?: string;
  providerDisplayName?: string;
  latestEventId?: string;
}

export interface ProviderEvidenceDisplayState {
  evidenceBadges: ProviderEvidenceDisplayBadge[];
  hasProviderEvidence: boolean;
  hasAttestedProviderEvidence: boolean;
  hasRevokedProviderEvidence: boolean;
  hasPendingProviderEvidence: boolean;
  hasSupersededProviderEvidence: boolean;
}

// ─── Mapping configuration (locked vocabulary) ──────────────────────

interface StateBadgeConfig {
  label: string;
  testId: string;
  variant: ProviderEvidenceBadgeVariant;
  // Whether the badge is rendered on each surface. `superseded` and
  // `none` never render a badge regardless of surface; they only
  // influence the state booleans.
  visibleOn: ReadonlySet<ProviderEvidenceSurface>;
}

const ATTESTED_CONFIG: StateBadgeConfig = {
  label: 'Provider attested',
  testId: 'training-provider-evidence-badge-attested',
  variant: 'secondary',
  visibleOn: new Set(['ticket', 'requirement', 'detail', 'admin']),
};

// `revoked` is intentionally hidden on the `ticket` surface in the I8
// default: the ticket card already renders the manager `Verification
// revoked` row from H2 when applicable, and double-destructive on the
// same card was the risk I6 flagged. The state boolean
// `hasRevokedProviderEvidence` still reflects the row so callers can
// decide to surface it elsewhere (e.g. via a non-badge affordance).
const REVOKED_CONFIG: StateBadgeConfig = {
  label: 'Provider attestation revoked',
  testId: 'training-provider-evidence-badge-revoked',
  variant: 'destructive',
  visibleOn: new Set(['requirement', 'detail', 'admin']),
};

// `pending` is admin/detail only. Ticket cards and requirement rows
// stay free of a pending-state badge to avoid implying assignment.
const PENDING_CONFIG: StateBadgeConfig = {
  label: 'Provider evidence pending',
  testId: 'training-provider-evidence-badge-pending',
  variant: 'outline',
  visibleOn: new Set(['detail', 'admin']),
};

// Deterministic ordering: revoked > attested > pending > superseded > none.
const STATE_PRIORITY: Record<ProviderEvidenceState, number> = {
  revoked: 0,
  attested: 1,
  pending: 2,
  superseded: 3,
  none: 4,
};

// ─── Public: per-row badge resolver ─────────────────────────────────

export function getProviderEvidenceBadgeForState(
  row: TrainingProviderEvidenceSummaryRow,
  surface: ProviderEvidenceSurface,
): ProviderEvidenceDisplayBadge | null {
  let config: StateBadgeConfig | null = null;
  switch (row.provider_state) {
    case 'attested':
      config = ATTESTED_CONFIG;
      break;
    case 'revoked':
      config = REVOKED_CONFIG;
      break;
    case 'pending':
      config = PENDING_CONFIG;
      break;
    case 'superseded':
    case 'none':
    default:
      return null;
  }
  if (!config.visibleOn.has(surface)) return null;
  return {
    label: config.label,
    testId: config.testId,
    variant: config.variant,
    providerId: row.provider_id,
    providerDisplayName: row.provider_display_name,
    latestEventId: row.latest_event_id,
  };
}

// ─── Public: builder over a list of rows ────────────────────────────

const EMPTY_DISPLAY_STATE: ProviderEvidenceDisplayState = {
  evidenceBadges: [],
  hasProviderEvidence: false,
  hasAttestedProviderEvidence: false,
  hasRevokedProviderEvidence: false,
  hasPendingProviderEvidence: false,
  hasSupersededProviderEvidence: false,
};

export function buildProviderEvidenceDisplayState(
  rows: TrainingProviderEvidenceSummaryRow[] | null | undefined,
  surface: ProviderEvidenceSurface = 'detail',
): ProviderEvidenceDisplayState {
  if (!rows || rows.length === 0) {
    return { ...EMPTY_DISPLAY_STATE, evidenceBadges: [] };
  }

  // Collect state booleans from ALL rows independently of surface
  // visibility so callers can decide to render non-badge UI when a
  // badge is hidden on the current surface.
  let hasAttested = false;
  let hasRevoked = false;
  let hasPending = false;
  let hasSuperseded = false;

  // Build badge list per surface visibility.
  type BadgeWithSortKey = {
    badge: ProviderEvidenceDisplayBadge;
    statePriority: number;
    providerDisplayName: string;
    latestReceivedAt: string;
  };
  const enriched: BadgeWithSortKey[] = [];

  for (const row of rows) {
    switch (row.provider_state) {
      case 'attested':
        hasAttested = true;
        break;
      case 'revoked':
        hasRevoked = true;
        break;
      case 'pending':
        hasPending = true;
        break;
      case 'superseded':
        hasSuperseded = true;
        break;
      case 'none':
      default:
        break;
    }
    const badge = getProviderEvidenceBadgeForState(row, surface);
    if (badge) {
      enriched.push({
        badge,
        statePriority: STATE_PRIORITY[row.provider_state] ?? 99,
        providerDisplayName: row.provider_display_name ?? '',
        latestReceivedAt: row.latest_received_at ?? '',
      });
    }
  }

  enriched.sort((a, b) => {
    if (a.statePriority !== b.statePriority) {
      return a.statePriority - b.statePriority;
    }
    const nameCmp = a.providerDisplayName.localeCompare(
      b.providerDisplayName,
    );
    if (nameCmp !== 0) return nameCmp;
    // Tie-break: more recent received_at first.
    return b.latestReceivedAt.localeCompare(a.latestReceivedAt);
  });

  const hasProviderEvidence =
    hasAttested || hasRevoked || hasPending || hasSuperseded;

  return {
    evidenceBadges: enriched.map((e) => e.badge),
    hasProviderEvidence,
    hasAttestedProviderEvidence: hasAttested,
    hasRevokedProviderEvidence: hasRevoked,
    hasPendingProviderEvidence: hasPending,
    hasSupersededProviderEvidence: hasSuperseded,
  };
}
